import React, { useEffect, useState, useRef } from 'react';
import { collection, query, where, getDocs, updateDoc, doc, serverTimestamp, onSnapshot, QuerySnapshot, DocumentData } from 'firebase/firestore';
import { getFailoverAwareDatabase, getFailoverAwareAuth } from '../../config/firebaseWithFailover';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import styles from '../carrier/Notifications.module.css';
import CounterOfferModal from '../../components/shipper/CounterOfferModal';
import { updateLoadRequestStatus, handleBrokerRejection } from '../../services/notificationService';
import { Dialog } from '@reach/dialog';
import '@reach/dialog/styles.css';
import Logger from '../../services/logger';
import { NOTIFICATION_STATUS, NOTIFICATION_TYPES, DASHBOARD_ACTION_REQUIRED_NOTIFICATION_TYPES } from '../../constants/notificationTypes';
import {
  NOTIFICATION_TRAY_LOAD_MORE_LABEL,
  filterNotificationsForBellTray,
} from '../../constants/notificationTrayUi';
import { reconcileStalePoLinkedTrayNotifications } from '../../services/notificationFirestoreQueries';
import { cleanupExpiredBellTrayNotifications } from '../../services/notificationTrayRetention';
import { useNotificationTrayPagination } from '../../hooks/useNotificationTrayPagination';

interface Notification {
  id: string;
  type: string;
  senderId: string;
  senderName: string;
  message: string;
  read: boolean;
  createdAt: Date;
  requiresAction?: boolean;
  loadDetails?: {
    pickupLocation: {
      address: string;
      city: string;
      state: string;
    };
    deliveryLocation: {
      address: string;
      city: string;
      state: string;
    };
    rate: number;
    poNumber?: string;
    isMarketplace?: boolean;
  };
  brokerId?: string;
  carrierId?: string;
  poNumber?: string;
}

interface BrokerNotificationsTrayProps {
  onClose: () => void;
}

const BrokerNotificationsTray: React.FC<BrokerNotificationsTrayProps> = ({ onClose }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCounterOfferModal, setShowCounterOfferModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const trayRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsNotification, setDetailsNotification] = useState<Notification | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const { visibleCount, hasMore, remaining, loadMore, pageSize } = useNotificationTrayPagination(notifications.length);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !showDetailsModal &&
        !showCounterOfferModal &&
        trayRef.current &&
        !trayRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, showDetailsModal, showCounterOfferModal]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getFailoverAwareAuth(), async (user) => {
      if (user) {
        setCurrentUser(user);
        try {
          void cleanupExpiredBellTrayNotifications(
            getFailoverAwareDatabase(),
            user.uid,
            ['brokerId', 'userId']
          ).catch((err) => {
            Logger.warn('[BrokerNotificationsTray] Retention cleanup skipped', err as Error);
          });

          // Query notifications for broker using brokerId
          const brokerQuery = query(
            collection(getFailoverAwareDatabase(), 'notifications'),
            where('brokerId', '==', user.uid)
          );
          
          // Query notifications for broker using userId (fallback)
          const userQuery = query(
            collection(getFailoverAwareDatabase(), 'notifications'),
            where('userId', '==', user.uid)
          );
          
          let brokerNotifications: Notification[] = [];
          let userNotifications: Notification[] = [];
          let brokerUnsubscribe: (() => void) | null = null;
          let userUnsubscribe: (() => void) | null = null;
          
          const processNotifications = () => {
            // Merge and deduplicate notifications
            const allNotifications = [...brokerNotifications, ...userNotifications];
            const uniqueNotifications = allNotifications.filter((notification, index, self) => 
              index === self.findIndex(n => n.id === notification.id)
            );
            
            // Exclude dashboard Action Required + items older than 4 weeks (shared C/B/S filter).
            const bellOnlyNotifications = filterNotificationsForBellTray(uniqueNotifications, 'broker').sort(
              (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );

            setNotifications(bellOnlyNotifications);
            void reconcileStalePoLinkedTrayNotifications(
              getFailoverAwareDatabase(),
              bellOnlyNotifications
            ).catch((staleErr) => {
              Logger.warn(
                '[BrokerNotificationsTray] Stale PO tray reconcile failed (non-fatal)',
                staleErr as Error
              );
            });
            setLoading(false);
          };
          
          // Set up real-time listeners
          brokerUnsubscribe = onSnapshot(brokerQuery, (snapshot: QuerySnapshot<DocumentData>) => {
            brokerNotifications = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate() || new Date()
            })) as Notification[];
            processNotifications();
          });
          
          userUnsubscribe = onSnapshot(userQuery, (snapshot: QuerySnapshot<DocumentData>) => {
            userNotifications = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate() || new Date()
            })) as Notification[];
            processNotifications();
          });
          
          // Cleanup function
          return () => {
            if (brokerUnsubscribe) brokerUnsubscribe();
            if (userUnsubscribe) userUnsubscribe();
          };
        } catch (error) {
          Logger.error('Error fetching broker notifications:', error as Error);
          setError('Failed to load notifications');
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    if (!notification.read) {
      updateDoc(doc(getFailoverAwareDatabase(), 'notifications', notification.id), {
        read: true,
        updatedAt: serverTimestamp()
      });
    }

    // Handle different notification types
    if (notification.type === 'make_offer' && notification.requiresAction) {
      // Marketplace offers with action required - show accept/reject modal
      setSelectedNotification(notification);
      setShowCounterOfferModal(true);
    } else if (notification.type === 'carrier_counter_offer' && notification.requiresAction) {
      // Counter offers with action required - show accept/reject modal
      setSelectedNotification(notification);
      setShowCounterOfferModal(true);
    } else if (notification.type === 'make_offer' || notification.type === 'carrier_counter_offer') {
      // Marketplace offers or counter offers without action required - show details only
      setDetailsNotification(notification);
      setShowDetailsModal(true);
    }
  };

  const handleAcceptCounterOffer = async () => {
    if (selectedNotification?.id) {
      try {
        Logger.debug('[BrokerNotificationsTray] Accepting offer:', selectedNotification);

        const poNumber = selectedNotification.loadDetails?.poNumber;
        if (!poNumber) {
          throw new Error('PO number not found on notification');
        }

        if (
          selectedNotification.type === 'make_offer' ||
          selectedNotification.type === 'carrier_counter_offer'
        ) {
          const carrierId = selectedNotification.carrierId;
          const offerRate = selectedNotification.loadDetails?.rate;
          if (!carrierId || !currentUser?.uid) {
            throw new Error('Carrier ID or broker session not found');
          }
          if (!offerRate || offerRate <= 0) {
            throw new Error('Offer rate not found on notification');
          }
          const { deferCounterOfferAcceptance } = await import(
            '../../services/deferredRateConfirmationService'
          );
          await deferCounterOfferAcceptance({
            poNumber,
            carrierId,
            agreedRate: offerRate,
            partyId: currentUser.uid,
            partyRole: 'broker',
          });
        }

        // Action Required: delete after progressing accept (not mark-read alone).
        if (
          (DASHBOARD_ACTION_REQUIRED_NOTIFICATION_TYPES as readonly string[]).includes(
            selectedNotification.type
          )
        ) {
          const { acknowledgeRejectionNotification, acknowledgeRejectionNotificationsForPo } =
            await import('../../services/notificationFirestoreQueries');
          await acknowledgeRejectionNotification(
            getFailoverAwareDatabase(),
            selectedNotification.id
          );
          if (currentUser?.uid) {
            await acknowledgeRejectionNotificationsForPo(
              getFailoverAwareDatabase(),
              poNumber,
              currentUser.uid
            );
          }
        } else {
          await updateDoc(doc(getFailoverAwareDatabase(), 'notifications', selectedNotification.id), {
            read: true,
            updatedAt: serverTimestamp(),
          });
        }

        setShowCounterOfferModal(false);
        setSelectedNotification(null);
        alert('Carrier has been notified to review and accept the rate confirmation.');
      } catch (error) {
        Logger.error('[BrokerNotificationsTray] Error accepting offer:', error as Error);
        setError(`Failed to accept offer: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };

  const handleRejectCounterOffer = async () => {
    if (!selectedNotification?.id || !currentUser?.uid) return;
    try {
      const poNumber =
        selectedNotification.poNumber || selectedNotification.loadDetails?.poNumber;
      if (!poNumber) {
        throw new Error('PO number not found on notification');
      }

      if (selectedNotification.type === NOTIFICATION_TYPES.MAKE_OFFER) {
        const carrierId = selectedNotification.carrierId;
        if (!carrierId) {
          throw new Error('Carrier ID not found in offer notification');
        }
        const offerRate = selectedNotification.loadDetails?.rate;
        if (!offerRate || offerRate <= 0) {
          throw new Error('Offer rate not found on notification');
        }

        const { rejectCarrierMakeOffer } = await import(
          '../../services/rejectCarrierMakeOfferService'
        );
        const result = await rejectCarrierMakeOffer({
          notificationId: selectedNotification.id,
          poNumber,
          carrierId,
          offerRate,
          partyId: currentUser.uid,
          partyRole: 'broker',
        });
        if (!result.success) {
          throw new Error(result.error || 'Failed to reject carrier offer');
        }
      } else if (selectedNotification.type === NOTIFICATION_TYPES.CARRIER_COUNTER_OFFER) {
        const carrierId = selectedNotification.carrierId;
        if (!carrierId) {
          throw new Error('Carrier ID not found in counter offer');
        }
        const counterOfferRate = selectedNotification.loadDetails?.rate;
        if (!counterOfferRate || counterOfferRate <= 0) {
          throw new Error('Counter offer rate not found on notification');
        }

        const { rejectCarrierCounterOffer } = await import(
          '../../services/rejectCarrierCounterOfferService'
        );
        const result = await rejectCarrierCounterOffer({
          notificationId: selectedNotification.id,
          poNumber,
          carrierId,
          counterOfferRate,
          partyId: currentUser.uid,
          partyRole: 'broker',
        });
        if (!result.success) {
          throw new Error(result.error || 'Failed to reject counter offer');
        }
      } else {
        return;
      }

      setNotifications((prev) =>
        prev.map((n) => (n.id === selectedNotification.id ? { ...n, read: true } : n))
      );
      setShowCounterOfferModal(false);
      setSelectedNotification(null);
    } catch (error) {
      Logger.error('[BrokerNotificationsTray] Error rejecting offer:', error as Error);
      setError('Failed to reject offer. Please try again.');
    }
  };

  const getNotificationTitle = (type: string) => {
    switch (type) {
      case 'make_offer':
        return 'Marketplace Offer';
      case 'carrier_counter_offer':
        return 'Counter Offer';
      default:
        return 'Notification';
    }
  };

  if (loading) {
    return (
      <div className={styles.panel} ref={trayRef}>
        <div className={styles.header}>
          <h3>Notifications</h3>
          <button type="button" onClick={onClose} className={styles.closeButton} aria-label="Close notifications">×</button>
        </div>
        <div className={styles.loading}>Loading notifications...</div>
      </div>
    );
  }

  return (
    <>
      <div
        className={styles.panel}
        ref={trayRef}
        onWheel={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <h3>Notifications</h3>
          <button type="button" onClick={onClose} className={styles.closeButton} aria-label="Close notifications">×</button>
        </div>

        {error && (
          <div className={styles.error}>
            {error}
            <button type="button" onClick={() => setError(null)}>×</button>
          </div>
        )}
        
        <div className={styles.notificationList}>
          {notifications.length === 0 ? (
            <div className={styles.noNotifications}><p>No notifications</p></div>
          ) : (
            <>
            {notifications.slice(0, visibleCount).map((n) => {
              const amount = n.loadDetails?.rate;
              const amountLabel = typeof amount === 'number' ? `$${amount.toLocaleString()}` : undefined;
              const po = n.loadDetails?.poNumber || '—';
              const created = n.createdAt ? new Date(n.createdAt).toLocaleString() : '';

              const badgeClass = n.type === 'make_offer'
                ? styles.typeOffer
                : n.type === 'carrier_counter_offer'
                ? styles.typeCounter
                : (n.type || '').includes('accept')
                ? styles.typeAccepted
                : (n.type || '').includes('reject')
                ? styles.typeRejected
                : styles.typeOffer;

              return (
                <div key={n.id} className={styles.card} onClick={() => handleNotificationClick(n)}>
                  <div className={styles.cardHeader}>
                    <span className={`${styles.typeBadge} ${badgeClass}`}>{getNotificationTitle(n.type)}</span>
                  </div>
                  <div className={styles.actions}>
                    {!n.read && <button type="button" className={styles.btnMarkRead} onClick={(e) => { e.stopPropagation(); updateDoc(doc(getFailoverAwareDatabase(),'notifications', n.id), { read: true, updatedAt: serverTimestamp() }); }}>Mark read</button>}
                    <button type="button" className={styles.btnLink} onClick={(e) => { e.stopPropagation(); setDetailsNotification(n); setShowDetailsModal(true); }}>Details</button>
                  </div>
                  <div className={styles.messageRow}>
                    <span>{n.message}</span>
                  </div>
                  <div className={styles.metaRow}>
                    <span>{created}</span>
                    {amountLabel && <span>Amount: <span className={styles.amount}>{amountLabel}</span></span>}
                    <span>PO: {po}</span>
                  </div>
                </div>
              );
            })}
            {hasMore && (
              <div className={styles.loadMoreRow}>
                <button
                  type="button"
                  className={styles.loadMoreButton}
                  onClick={loadMore}
                >
                  {NOTIFICATION_TRAY_LOAD_MORE_LABEL}
                  {remaining > 0 ? ` (${Math.min(remaining, pageSize)} of ${remaining} left)` : ''}
                </button>
              </div>
            )}
            </>
          )}
        </div>
      </div>

      {/* Counter Offer Modal */}
      {showCounterOfferModal && selectedNotification && (
        <CounterOfferModal
          isOpen={showCounterOfferModal}
          onClose={() => {
            setShowCounterOfferModal(false);
            setSelectedNotification(null);
          }}
          currentRate={0} // Marketplace offers don't have a current rate
          counterOffer={selectedNotification.loadDetails?.rate || 0}
          loadDetails={{
            pickupLocation: selectedNotification.loadDetails?.pickupLocation || { address: '', city: '', state: '' },
            deliveryLocation: selectedNotification.loadDetails?.deliveryLocation || { address: '', city: '', state: '' },
            poNumber: selectedNotification.loadDetails?.poNumber || '',
            carrierName: selectedNotification.senderName || 'Unknown Carrier'
          }}
          onAccept={handleAcceptCounterOffer}
          onReject={handleRejectCounterOffer}
        />
      )}

      {/* Details Modal */}
      {showDetailsModal && detailsNotification && (
        <Dialog
          isOpen={showDetailsModal}
          onDismiss={() => {
            setShowDetailsModal(false);
            setDetailsNotification(null);
          }}
        >
          <div className={styles.detailsModal}>
            <h3>Offer Details</h3>
            <div className={styles.detailsContent}>
              <p><strong>From:</strong> {detailsNotification.senderName}</p>
              <p><strong>Message:</strong> {detailsNotification.message}</p>
              {detailsNotification.loadDetails?.rate && (
                <p><strong>Rate:</strong> ${detailsNotification.loadDetails.rate.toLocaleString()}</p>
              )}
              {detailsNotification.loadDetails?.poNumber && (
                <p><strong>PO Number:</strong> {detailsNotification.loadDetails.poNumber}</p>
              )}
              <p><strong>Time:</strong> {new Date(detailsNotification.createdAt).toLocaleString()}</p>
            </div>
            <button
              onClick={() => {
                setShowDetailsModal(false);
                setDetailsNotification(null);
              }}
              className={styles.closeButton}
            >
              Close
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
};

export default BrokerNotificationsTray;
